import { matchPath } from "react-router-dom";

import type { Id, MessageContext, Screen } from "../api/types";
import { DISCIPLINE_PARAM } from "./disciplineFilter";
import { addByHandPath, OPEN_POINTS_PATH, uploadPath } from "./paths";

/*
  How the names of screens in the contracts (`Screen`, 2.4.1) meet the addresses of the app (2.11.2).
  Used for "point to" (open the screen the agent named) and for the heartbeat (which screen a person is on).
*/

// The older addresses a view replaced (`MOVED_SCREENS`: the home, the inbox and its card, the lane of what came back, the older
// getting started) keep their names here: nothing is drawn at them, as they open their view at once.
const SCREEN_OF_PATTERN: ReadonlyArray<readonly [pattern: string, screen: string]> = [
  ["/", "home"],
  // The views of the 2026-11-03 design (rebuild 4.0: the contract names them, so the agent knows them too).
  ["/overview", "overview"],
  ["/items", "items"],
  ["/shared", "shared"],
  ["/capabilities", "capabilities"],
  ["/deliverables", "deliverables"],
  ["/review", "review"],
  ["/getting-started", "getting_started"],
  ["/live", "live_view"],
  ["/navigate", "family_navigator"],
  ["/gallery", "gallery"],
  ["/whats-new", "whats_new"],
  ["/sessions/new", "new_session"],
  ["/sessions/:id", "live_map"],
  ["/sessions/:id/log", "session_log"],
  ["/sessions/:id/checks", "checks_list"],
  ["/sessions/:id/coverage/:reportId", "coverage_report"],
  ["/sessions/:id/hand-overs/:hoId", "hand_over"],
  ["/items/:id/map", "item_map"],
  ["/items/:id", "item_page"],
  ["/restore", "restore_control"],
  ["/elements/new", "add_by_hand_form"],
  ["/elements/:id/edit", "edit_element"],
  ["/elements/:id", "element_page"],
  ["/symbols", "symbol_picker"],
  ["/deliverables/new", "upload_form"],
  ["/deliverables/:id", "deliverable_view"],
  ["/library", "library_browser"],
  ["/files", "files_view"],
  ["/chapters", "chapters"],
  ["/chapters/:key", "chapter_editor"],
  ["/project-lists", "project_lists"],
  ["/open-points", "open_points"],
  ["/inbox", "sme_inbox"],
  ["/inbox/:elementId", "sme_inbox"],
  ["/back-from-sme", "back_from_sme"],
  ["/onboarding", "onboarding"],
  ["/profile", "my_profile"],
  ["/admin", "admin_accounts"],
  ["/change-password", "change_password"],
  ["/sign-in", "sign_in"],
];

export interface RouteLocation {
  screen: string;
  itemId: Id | null;
  sessionId: Id | null;
  elementId?: Id | null;
}

/** Which screen an address is, and which item and session it names. */
export function locate(pathname: string): RouteLocation {
  for (const [pattern, screen] of SCREEN_OF_PATTERN) {
    const match = matchPath({ path: pattern, end: true }, pathname);
    if (!match) continue;
    const id = match.params.id ?? null;
    return {
      screen,
      itemId: pattern.startsWith("/items/") ? id : null,
      sessionId: pattern.startsWith("/sessions/") && id !== "new" ? id : null,
      ...(pattern.startsWith("/elements/") && id ? { elementId: id } : {}),
    };
  }
  return { screen: pathname, itemId: null, sessionId: null };
}

/** Every screen the agent can be told about (the contract's `Screen`). The record makes a new value a type error here. */
const AGENT_SCREENS: Record<Screen, true> = {
  item_page: true,
  item_map: true,
  live_map: true,
  live_view: true,
  family_navigator: true,
  gallery: true,
  library_browser: true,
  deliverable_view: true,
  proposal_tray: true,
  checks_list: true,
  files_view: true,
  sme_inbox: true,
  restore_control: true,
  symbol_picker: true,
  add_by_hand_form: true,
  admin_accounts: true,
  admin_model_connection: true,
  admin_disciplines: true,
  upload_form: true,
  open_points: true,
  chapters: true,
  project_lists: true,
  overview: true,
  items: true,
  capabilities: true,
  shared: true,
  deliverables: true,
  review: true,
  getting_started: true,
};

/**
 * The name the agent knows an address by, or null when the contract has none (the home, what's new, the sign-in). The
 * chapter editor is a chapter for the agent, as `pointToPath` already reads it.
 */
export function agentScreenOf(pathname: string): Screen | null {
  const name = locate(pathname).screen;
  if (name === "chapter_editor") return "chapters";
  return Object.prototype.hasOwnProperty.call(AGENT_SCREENS, name) ? (name as Screen) : null;
}

/**
 * The address parameter that holds the element a view of the new design has open (each view keeps its pick in the address):
 * the Items view's item, the part kind or field of Shared parts, the capability, the kind of document, the card in Review.
 */
const PICK_PARAM_OF_VIEW: Readonly<Record<string, string>> = {
  items: "item",
  shared: "el",
  capabilities: "cap",
  deliverables: "kind",
  review: "card",
};

/** What a view adds to a message on its own: the phase it shows, the Getting started slide, the step of a capability. */
export type ViewContext = Pick<MessageContext, "phase" | "slide" | "capability_step">;

/**
 * What a message to the agent says about where the person is: the screen, and the item or element on it. With the query
 * string, the discipline picked in the bar (`?d=`) and the element a view of the new design has open are added; `view` adds
 * what only the view knows (rebuild 4.0). Nothing is added that is not set, so the older screens send what they sent before.
 */
export function agentContextFor(pathname: string, search = "", view: ViewContext = {}): MessageContext {
  const here = locate(pathname);
  const element = matchPath({ path: "/elements/:id", end: true }, pathname)?.params.id ?? null;
  const context: MessageContext = { screen: agentScreenOf(pathname) };
  const params = new URLSearchParams(search);
  const pickParam = PICK_PARAM_OF_VIEW[here.screen];
  const picked = pickParam ? params.get(pickParam)?.trim() || null : null;
  const elementId = here.itemId ?? element ?? picked;
  if (elementId) context.element_id = elementId;
  const discipline = params.get(DISCIPLINE_PARAM)?.trim();
  if (discipline) context.discipline = discipline;
  if (view.phase) context.phase = view.phase;
  if (view.slide) context.slide = view.slide;
  if (view.capability_step) context.capability_step = view.capability_step;
  return context;
}

interface PointTarget {
  screen: Screen;
  element_id?: Id | null;
}

/**
 * The address of a screen the agent pointed to, or null when the pointer names nothing that has an
 * address of its own (a map without an item, a session tray with no session open).
 */
export function pointToPath(target: PointTarget, context: { sessionId?: Id | null } = {}): string | null {
  const id = target.element_id ? encodeURIComponent(target.element_id) : null;
  const session = context.sessionId ? encodeURIComponent(context.sessionId) : null;
  switch (target.screen) {
    case "item_page":
      return id ? `/items/${id}` : null;
    case "item_map":
      return id ? `/items/${id}/map` : null;
    case "live_map":
      return session ? `/sessions/${session}` : id ? `/items/${id}/map` : null;
    case "proposal_tray":
      return session ? `/sessions/${session}` : null;
    case "checks_list":
      return session ? `/sessions/${session}/checks` : null;
    // The navigator and the live view open on the position the agent named, when it named one (workflow 22).
    case "live_view":
      return id ? `/live?select=${id}` : "/live";
    case "family_navigator":
      return id ? `/navigate?select=${id}` : "/navigate";
    case "gallery":
      return "/gallery";
    case "library_browser":
      return "/library";
    case "deliverable_view":
      return id ? `/deliverables/${id}` : null;
    case "files_view":
      return "/files";
    // The inbox and its card are the Review view now; the agent's older name for them opens it on the card.
    case "sme_inbox":
      return id ? `/review?card=${id}` : "/review";
    case "restore_control":
      return "/restore";
    case "symbol_picker":
      return "/symbols";
    // The session goes with the form, so the agent learns of what is added and the way back is offered (CP-06).
    case "add_by_hand_form":
      return addByHandPath({ session: context.sessionId });
    case "admin_accounts":
      return "/admin#accounts";
    case "admin_model_connection":
      return "/admin#model-connection";
    case "admin_disciplines":
      return "/admin#disciplines";
    // The four screens of the completion round (CP-17). A chapter with an element opens that element's page; without one
    // the chapters index (`/chapters`, features/navigate) opens.
    case "upload_form":
      return uploadPath(context.sessionId);
    case "open_points":
      return OPEN_POINTS_PATH;
    case "chapters":
      return id ? `/elements/${id}` : "/chapters";
    case "project_lists":
      return "/project-lists";
    // The views of the 2026-11-03 design (rebuild 4.0). Each keeps the element it opens in its own address parameter.
    case "overview":
      return "/overview";
    case "items":
      return id ? `/items?item=${id}` : "/items";
    case "capabilities":
      return id ? `/capabilities?cap=${id}` : "/capabilities";
    case "shared":
      return id ? `/shared?el=${id}` : "/shared";
    case "deliverables":
      return id ? `/deliverables?kind=${id}` : "/deliverables";
    case "review":
      return id ? `/review?card=${id}` : "/review";
    case "getting_started":
      return "/getting-started";
  }
}
